import { Component, OnInit, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerIntl, MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Auth } from '@aws-amplify/auth';
import { Storage } from '@aws-amplify/storage';
import { ManagerEditDialogComponent } from '../dialog/manager-edit-dialog/manager-edit-dialog.component';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { AttachmentItem, EventPlanForm, EventSchedule, EventVendor, MasterItem } from '../model/my-event';
import { CustomDatepickerIntl } from '../util/datepicker-intl-util';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { GeneralYesNoDialogComponent } from '../dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { OptionPanelWidthPipe } from '../util/option-panel-width.pipe';
import { TextareaResizeDirective } from '../util/textarea-resize.directive';
import { LayoutCache } from '../util/layout-cache';

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY/MM/DD'
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY/MM/DD',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-my-event-edit-form',
  templateUrl: './my-event-edit-form.component.html',
  styleUrl: './my-event-edit-form.component.css',
  standalone: true,
  imports: [
    OptionPanelWidthPipe,
    TextareaResizeDirective,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatIconModule,
  ],
  providers: [
    // この画面のプルダウンの選択肢だけ文字を大きくする（styles.css の .edit-form-select-panel）
    { provide: MAT_SELECT_CONFIG, useValue: { overlayPanelClass: 'edit-form-select-panel' } },
    { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MatDatepickerIntl, useClass: CustomDatepickerIntl },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    }
  ],
})
export class MyEventEditFormComponent implements OnInit {
  @Input() eventId: string = '';
  @Input() documentType: 'plan' | 'report' = 'plan';
  @Output() cancelled = new EventEmitter<void>();


  @ViewChild('fileInput1') fileInput1!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput2') fileInput2!: ElementRef<HTMLInputElement>;

  readonly Constant = Constant;
  showSpinner = true;
  isSaving = false;

  form: EventPlanForm = this.createEmptyForm();

  // マスタデータ
  masterData: { [key: string]: MasterItem[] } = {};
  categoryMajorName: string = '';

  // 日程の日付（Dateオブジェクト）
  scheduleDates: (Date | null)[] = [null];

  // 開催者・対象組織の選択済みノード（ダイアログ用）
  organizerSelectedNodes: any[] = [];
  targetSelectedNodes: any[] = [];

  // API から null が返った場合のみデフォルト適用（[] は明示的な全解除）
  private organizerOrgDetailNeverSet = true;
  private targetOrgDetailNeverSet = true;

  // ログインユーザーの所属組織
  private myAccountOrgs: any[] = [];

  // 役職マスタ（組合役員の詳細選択肢用）
  private officialPositionItems: MasterItem[] = [];
  // 対象/参加者（組合役員）で現在選択されているIDの名称（enabled条件なしで解決したもの）。
  // 役職マスタが無効化されていても、選択済みのIDだけはチェックボックスごと表示を維持し、
  // チェックを外して保存すれば次回以降は選択肢からも消えるようにするために使う
  private participantDetailPositions: MasterItem[] = [];

  // 組織マスタツリー（カバー率復元用）
  private orgMasterData: any = {};

  private masterCountData : any = {};

  public isPermissionOpen = false;

  public authDescription;


  // 添付ファイル
  attachments: { 1: AttachmentItem[]; 2: AttachmentItem[] } = { 1: [], 2: [] };
  uploadingCategory: 0 | 1 | 2 = 0;

  private readonly allowedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'];
  private readonly maxAttachmentSize = 10 * 1024 * 1024; // 10MB

  readonly numberOfDaysOptions = [
    { value: '1', label: '1日' },
    { value: '2', label: '2日' },
    { value: '3', label: '3日' },
    { value: '4', label: '4日' },
    { value: '5', label: '5日' },
    { value: 'period', label: '期間' },
  ];

  readonly durationOptions = this.generateDurationOptions();

  constructor(
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
    private dialog: MatDialog,
    private http: HttpClient,
  ) {}

  // 画面の幅が変わったら描画し直し、チェックボックス欄の列数を計算し直す（計算し直すかは layoutCache が本文の幅で判断する）
  @HostListener('window:resize')
  onResize(): void {}

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit(): void {
    this.loadData();
  }

  togglePermission() {
    this.isPermissionOpen = !this.isPermissionOpen;
  }

  private loadAuthDescription() {
    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);

      this.commonUtil
        .apiGet('/master/authdescription', options)
        .then(res => {
          console.log(res)
          if (res.status === Constant.OK) {
            this.authDescription = res.data.auth_description
          }
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
        });
    });
  }

  // ===== Computed =====

  get isLaborNegotiation(): boolean {
    return this.form.categoryId === 8;
  }

  get showSocialTheme(): boolean {
    return this.form.categoryId !== 9;
  }

  // ===== Visibility =====

  private get c(): number | null { return this.form.categoryId; }

  get showIssueSection(): boolean {
    return this.c !== 9;
  }

  get showImprovementOutcome(): boolean {
    return this.c !== 8 && this.c !== 9;
  }

  get showNoticeOrganizerDetail(): boolean {
    return this.c !== 8 && this.c !== 9;
  }

  get showNoticeTargetOrganizerDetail(): boolean {
    return this.c !== 9;
  }


  get showTargetOrg(): boolean {
    return this.c !== 8;
  }

  get showTargetUnit(): boolean {
    return this.c !== 8;
  }

  get showAttr(): boolean {
    return this.c !== 8 && this.c !== 9;
  }

  get showCompanyCollaboration(): boolean {
    return this.c !== 7 && this.c !== 8;
  }

  get showCompanyOtherCollaboration(): boolean {
    return this.c !== 8;
  }

  get showHoldingMethod(): boolean {
    return this.c === 1 || this.c === 10;
  }

  get showEducationFormat(): boolean {
    return this.c === 2 || this.c === 10;
  }

  get showSurveyMethod(): boolean {
    return this.c === 5 || this.c === 7 || this.c === 10;
  }

  get showAnnouncementMethod(): boolean {
    return this.c !== 8 && this.c !== 9;
  }

  get showRecruitmentMethod(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c === 6 || c === 10);
  }

  get showVenue(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c >= 9);
  }

  get showPrivateCategoryNotice(): boolean {
    return this.c === 9 || this.c === 8;
  }

  get showEstimatedParticipants(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c === 5 || c === 7 || c >= 9);
  }

  get showCostSection(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c >= 9);
  }

  get showSubsidyAndVendors(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c === 10);
  }

  get showWorkplaceSection(): boolean {
    return this.c === 5 || this.c === 7;
  }

  get showEffectiveness(): boolean {
    const c = this.c;
    return c !== null && (c <= 3 || c === 10);
  }

  get showMeetingSection(): boolean {
    return this.c === 8;
  }

  get implementationMethodBasicOptions(): MasterItem[] {
    return this.getMasterItems('implementation_method')
      .filter(m => m.attribute !== 'consultation_only');
  }

  get categorySubOptions(): MasterItem[] {
    if (!this.form.categoryId) return [];
    return this.getMasterItems('category_sub')
      .filter(m => m.parentId === this.form.categoryId);
  }

  get categorySubGridCols(): 3 | 4 {
    return this.categorySubOptions.some(i => i.name.length > 8) ? 3 : 4;
  }

  // 入力欄に使える幅（フォント差の余裕21pxを引いた値）。画面幅ごとの配置から計算する
  //  1440px以上：約911px（固定）
  //  1024〜1439px：本文の幅（最大1100） − 左右の余白32×2 − 項目名168 − 入力欄の左右の余白26（リセットの列なし）
  //  768〜1023px：767px以下を踏襲（本文の幅 − 左右の余白16×2。項目名は入力欄の上）
  //  767px以下：本文の幅 − 左右の余白16×2（項目名は入力欄の上）
  // 本文の幅は、縦スクロールバーを除き、画面の拡大・縮小に影響されない幅（documentElement.clientWidth）を使う。
  // window.innerWidth は、SPで詳細ページ（横にはみ出して縮小表示される）から編集に来ると実際より大きくなり、列数が増えてしまうため。
  // ブレイクポイントの判定も CSS の @media と同じ matchMedia で行う
  private get fieldAvailableWidth(): number {
    const w = document.documentElement.clientWidth;
    if (window.matchMedia('(min-width: 1440px)').matches) return 890;
    if (window.matchMedia('(min-width: 1024px)').matches) return Math.min(w, 1100) - 64 - 168 - 26 - 21;
    if (window.matchMedia('(min-width: 768px)').matches) return w - 32 - 21;
    return w - 32 - 21;
  }

  // チェックボックス欄の列数。項目名を改行させずに、入力欄の幅に収まる最大の列数を返す
  // （maxCols を上限に、収まらなければ列数を減らす）。
  // offset: 欄の左にある小見出し（「中分類」等）の幅、gap: 列の間隔（.checkbox-grid の column-gap）
  // spExtra：SPでの、項目1つの文字以外の幅（実測。順位付きのチェックボックス30px・チェックボックス22px・ラジオ21px）
  fitGridCols(items: MasterItem[], maxCols: number, offset = 0, gap = 20, spOffset = 0, spExtra = 30): number {
    const key = ['fit', maxCols, offset, gap, spOffset, spExtra, ...items.map(item => item.name)].join('|');
    return this.layoutCache.get(key, () => this.computeGridCols(items, maxCols, offset, gap, spOffset, spExtra));
  }

  private computeGridCols(items: MasterItem[], maxCols: number, offset: number, gap: number, spOffset: number, spExtra: number): number {
    if (window.matchMedia('(max-width: 767px), (min-width: 768px) and (max-width: 1023px)').matches) {
      return this.fitGridColsSp(items, maxCols, spOffset, spExtra);
    }
    const available = this.fieldAvailableWidth - offset;
    // 項目1つの幅：文字（16px。全角16px・半角9px）＋優先順位の丸数字18px＋間隔8px＋左右の余白10px
    // 5列の欄は項目の最小幅が116px（.cols-5 .priority-checkbox）
    const minItemWidth = maxCols === 5 ? 116 : 0;
    const itemWidth = (name: string) => Math.max(minItemWidth,
      [...(name ?? '')].reduce((w, c) => w + (c.charCodeAt(0) > 0xff ? 16 : 9), 0) + 36);
    for (let cols = maxCols; cols > 1; cols--) {
      const colWidths = new Array(cols).fill(0);
      items.forEach((item, i) => {
        colWidths[i % cols] = Math.max(colWidths[i % cols], itemWidth(item.name));
      });
      if (colWidths.reduce((a, b) => a + b, 0) + gap * (cols - 1) <= available) {
        return cols;
      }
    }
    return 1;
  }

  // SP（767px以下）・タブレット（768〜1023px。SPを踏襲）の列数。文字の幅は見積もりではなく、その端末のフォントで実際に測る（Windows・Mac・iPhone でフォントの幅が違うため）。
  // 使える幅：本文の幅 − 左右の余白16×2 − 選択肢の左の余白6px − 左に置く小見出しの幅（spOffset） − 余裕4px。列の間は24px
  private fitGridColsSp(items: MasterItem[], maxCols: number, spOffset: number, spExtra: number): number {
    const available = document.documentElement.clientWidth - 32 - 6 - spOffset - 4;
    const itemWidth = (name: string) => this.spTextWidth(name) + spExtra;
    for (let cols = maxCols; cols > 1; cols--) {
      const colWidths = new Array(cols).fill(0);
      items.forEach((item, i) => {
        colWidths[i % cols] = Math.max(colWidths[i % cols], itemWidth(item.name));
      });
      if (colWidths.reduce((a, b) => a + b, 0) + 24 * (cols - 1) <= available) {
        return cols;
      }
    }
    return 1;
  }

  // 列数・文字の幅は、本文の幅が変わったときとフォントの読み込みが終わったときだけ計算し直す（入力のたびに測らない）
  private layoutCache = new LayoutCache();

  // SPの選択肢の文字（14px）の幅を、画面と同じフォントで測る
  private spTextWidth(name: string): number {
    return this.layoutCache.textWidth(name, 14);
  }

  // 案内方法・応募方法（SP）の列数。2つの欄で列をそろえるため、両方の選択肢から同じ列数を決め、列は同じ幅にする。
  // 1項目の幅：文字（実際に測る）＋文字以外の幅30px（実測）
  // 使える幅：本文の幅 − 左右の余白16×2 − 選択肢の左の余白6px − 余裕4px
  get methodSpCols(): number {
    // マスタの読み込み前後で選択肢が変わるため、選択肢の名前も key に含める
    const items = [...this.getMasterItems('announcement_method'), ...this.getMasterItems('recruitment_method')];
    const key = ['methodSp', ...items.map(item => item.name)].join('|');
    return this.layoutCache.get(key, () => this.computeMethodSpCols());
  }

  private computeMethodSpCols(): number {
    const available = document.documentElement.clientWidth - 32 - 6 - 4;
    const items = [...this.getMasterItems('announcement_method'), ...this.getMasterItems('recruitment_method')];
    const widest = Math.max(0, ...items.map(item => this.spTextWidth(item.name) + 30));
    for (let cols = 5; cols > 1; cols--) {
      if (widest * cols + 24 * (cols - 1) <= available) {
        return cols;
      }
    }
    return 1;
  }

  // 対象/参加者の詳細（SP）の列数。最大3列。列は同じ幅で、組合役員の長い役職名は列の中で折り返す。
  // 1項目の幅：文字（実際に測る）＋チェックボックス16px＋右の余白6px。長い役職名1つのために列が減らないよう、120px（7文字分）までとして数える。
  // 使える幅：本文の幅 − 左右の余白16×2 − 小見出し「詳細」28px＋間隔8px − フォント差の余裕10px
  get participantDetailSpCols(): number {
    const key = ['participantDetailSp', this.form.participantCategory, ...this.participantDetailOptions.map(item => item.name)].join('|');
    return this.layoutCache.get(key, () => this.computeParticipantDetailSpCols());
  }

  private computeParticipantDetailSpCols(): number {
    const available = document.documentElement.clientWidth - 32 - 36 - 10;
    const widest = Math.max(0, ...this.participantDetailOptions.map(item =>
      Math.min(120, this.spTextWidth(item.name) + 22)));
    for (let cols = 3; cols > 1; cols--) {
      if (widest * cols + 24 * (cols - 1) <= Math.min(available, this.form.participantCategory === 196 ? Infinity : 440)) {
        return cols;
      }
    }
    return 1;
  }

  get participantDetailOptions(): MasterItem[] {
    if (!this.form.participantCategory) return [];
    if (this.form.participantCategory === 196) {
      // enabledな役職に加えて、無効化されていても読み込み時点で選択済みだった役職は
      // チェックボックスごと表示し続ける。チェックを外して保存すれば
      // participantDetailsから消え、次回読み込み時は選択肢からも消える
      // 表示順は削除前のsort_order順を維持し、末尾に固定しない
      const enabledIds = new Set(this.officialPositionItems.map(m => m.id));
      const staleSelected = this.participantDetailPositions.filter(m => !enabledIds.has(m.id));
      return [...this.officialPositionItems, ...staleSelected].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
    }
    return this.getMasterItems('participant_detail')
      .filter(m => m.parentId === this.form.participantCategory);
  }

  // 公開範囲の選択肢は権限の高い順（管理以上→準管理以上→運用以上→全員）に縦並びで表示する
  get publicScopeOptions(): MasterItem[] {
    return [...this.getMasterItems('public_scope')].sort((a, b) => b.id - a.id);
  }

  // 各選択肢の横に表示する見出し（該当する役職が無くても表示する。全員は無し）
  getPublicScopeRolePrefix(scopeId: number): string {
    const prefixes: { [key: number]: string } = {
      289: '管理権限の役職',
      288: '準管理権限の役職',
      287: '運用権限の役職',
    };
    return prefixes[scopeId] ?? '';
  }

  // 各選択肢の横に表示する役職名（その権限レベルと完全一致する役職のみ、累積しない）。
  // 下書き/確認中はライブ、完了後は完了時点のスナップショットを使う
  // （スナップショットは選択中の公開範囲以上の役職しか持たないため、それより下の選択肢は空になる）
  getPublicScopeRoleNames(scopeId: number): string[] {
    const tierAuthority: { [key: number]: number } = { 287: 2, 288: 1, 289: 0 };
    if (!(scopeId in tierAuthority)) {
      return [];
    }
    const authority = tierAuthority[scopeId];

    if (this.form.status === 'completed') {
      return (this.form.publicScopeRoles ?? [])
        .filter(r => r.authority === authority)
        .map(r => r.name);
    }

    return this.officialPositionItems
      .filter(m => m.authority === authority)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(m => m.name);
  }

  get scheduleRows(): { index: number; label: string }[] {
    if (this.form.numberOfDays === 'period') {
      return [{ index: 0, label: '初日' }, { index: 1, label: '最終日' }];
    }
    const count = parseInt(this.form.numberOfDays) || 1;
    return Array.from({ length: count }, (_, i) => ({ index: i, label: `${i + 1}日目` }));
  }

  get statusLabel(): string {
    return Constant.myEventStatuses[this.form.status] ?? '';
  }

  get coverageRateValue(): string {
    const participants = this.form.estimatedParticipants;
    if (!participants || participants <= 0) return '';

    const denominator = this.targetSelectedNodes.reduce((sum: number, node: any) => {
      return sum + (node.orgCountForCoverage ?? 0);
    }, 0);

    if (denominator <= 0) return '-';

    return Math.round(participants / denominator * 100) + '';
  }

  get coverageRate(): string {
    const participants = this.form.estimatedParticipants;
    if (!participants || participants <= 0) return '';

    const denominator = this.targetSelectedNodes.reduce((sum: number, node: any) => {
      return sum + (node.orgCountForCoverage ?? 0);
    }, 0);

    if (denominator <= 0) return '- %';

    //console.log(Math.round(participants / denominator * 100))
    return Math.round(participants / denominator * 100) + '%';
  }

  get costPerPerson(): string {
    if (!this.form.estimatedTotalCost || !this.form.estimatedParticipants) return '';
    return Math.round(this.form.estimatedTotalCost / this.form.estimatedParticipants).toLocaleString() + '円';
  }

  get subsidyRate(): string {
    if (!this.form.subsidyAmount || !this.form.estimatedTotalCost) return '';
    return Math.round(this.form.subsidyAmount / this.form.estimatedTotalCost * 100) + '%';
  }

  get draftTitleValid(): boolean {
    if (this.form.status !== 'draft') {
      return true;
    }

    return !!this.form.title?.trim();
  }

  // ===== Master =====

  getMasterItems(type: string): MasterItem[] {
    return this.masterData[type] || [];
  }

  getMasterItemsByAttr(type: string, attribute: string): MasterItem[] {
    return this.getMasterItems(type).filter(m => m.attribute === attribute);
  }

  // ===== Priority checkbox =====

  togglePriority(arr: (number | null)[], id: number, max: number): void {
    const idx = arr.indexOf(id);
    // 選択済みの場合は解除し、後続の優先順位を繰り上げる
    if (idx >= 0) {
      arr.splice(idx, 1);
      arr.push(null);
      return;
    }
    if (arr.filter(x => x !== null).length < max) {
      const empty = arr.indexOf(null);
      if (empty >= 0) arr[empty] = id;
    }
  }

  isInPriority(arr: (number | null)[], id: number): boolean {
    return arr.includes(id);
  }

  isPriorityDisabled(arr: (number | null)[], id: number, max: number): boolean {
    return !arr.includes(id) && arr.filter(x => x !== null).length >= max;
  }

  getPriorityNum(arr: (number | null)[], id: number): number | null {
    const idx = arr.indexOf(id);
    return idx >= 0 ? idx + 1 : null;
  }

  resetPriority(arr: (number | null)[]): void {
    arr.fill(null);
  }

  // ===== Multi select =====

  toggleMultiSelect(arr: number[], id: number): void {
    const idx = arr.indexOf(id);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(id);
  }

  isMultiSelected(arr: number[], id: number): boolean {
    return arr.includes(id);
  }

  // ===== Radio =====

  /**
   * 選択済みのラジオボタンを再クリックした場合は選択を解除する。
   * × リセットボタンが表示される項目のみ対象（enabled で制御）。
   */
  toggleRadio<K extends keyof EventPlanForm>(
    event: Event, field: K, value: EventPlanForm[K], enabled: boolean = true
  ): void {
    if (!enabled || this.form[field] !== value) return;
    this.form[field] = null as EventPlanForm[K];
    // モデルを null にしてもブラウザ側の checked は残るため、
    // 明示的に外して次のクリックで再選択できるようにする
    (event.target as HTMLInputElement).checked = false;
  }

  // ===== Category change =====

  onCategoryMajorChange(): void {
    this.form.categorySub.fill(null);
  }

  // ===== Participant =====

  onParticipantCategoryChange(): void {
    this.form.participantDetails = [];
  }

  // ===== Schedule =====

  onNumberOfDaysChange(): void {
    const count = this.form.numberOfDays === 'period' ? 2 : (parseInt(this.form.numberOfDays) || 1);
    while (this.form.schedules.length < count) {
      this.form.schedules.push({ dayIndex: this.form.schedules.length + 1, eventDate: '', startTime: '', endTime: '' });
    }
    this.form.schedules.splice(count);
    this.form.schedules.forEach((s, i) => { s.dayIndex = i + 1; });
    while (this.scheduleDates.length < count) this.scheduleDates.push(null);
    this.scheduleDates.splice(count);
  }

  // ===== 開始・終了の時間（手入力） =====

  // 入力を始めるときは「09:30」を「0930」に戻し、数字だけで直せるようにする
  onTimeFocus(input: HTMLInputElement): void {
    input.value = input.value.replace(':', '');
  }


  // 全角数字は半角に直し、それ以外の数字でない文字は受け付けない（日本語入力中は確定してから直す）
  onTimeInput(event: Event, input: HTMLInputElement): void {
    if ((event as InputEvent).isComposing) return;
    const digits = input.value
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[^0-9]/g, '')
      .slice(0, 4);
    if (digits !== input.value) {
      input.value = digits;
    }
  }

  // 入力欄を離れたときに時間として確かめる。4桁で 0000〜2359 の実在する時間だけ受け付け、それ以外は空に戻す。
  // 終了が開始より前になった場合も、プルダウンのときと同じく終了を空に戻す
  onTimeBlur(index: number, field: 'startTime' | 'endTime', input: HTMLInputElement): void {
    const schedule = this.form.schedules[index];
    const time = this.parseTimeInput(input.value);
    schedule[field] = time;
    input.value = time;
    if (field === 'startTime') {
      this.onStartTimeChange(index);
    } else if (schedule.startTime && schedule.endTime && schedule.endTime < schedule.startTime) {
      schedule.endTime = '';
      input.value = '';
    }
  }

  // 入力した「0930」のほか、入力せずに離れたときの表示中の「09:30」もそのまま受け付ける。
  // 3桁の場合は頭に0をつける（「930」→「09:30」）
  private parseTimeInput(value: string): string {
    const m = /^([0-9]{1,2}):?([0-9]{2})$/.exec(value);
    if (!m) return '';
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 23 || min > 59) return '';
    return `${m[1].padStart(2, '0')}:${m[2]}`;
  }

  onStartTimeChange(index: number): void {
    const schedule = this.form.schedules[index];
    if (schedule.endTime && schedule.endTime < schedule.startTime) {
      schedule.endTime = '';
    }
  }

  getScheduleMinDate(index: number): any {
    for (let i = index - 1; i >= 0; i--) {
      if (this.scheduleDates[i]) return moment(this.scheduleDates[i]);
    }
    return null;
  }

  getScheduleMaxDate(index: number): any {
    for (let i = index + 1; i < this.scheduleDates.length; i++) {
      if (this.scheduleDates[i]) return moment(this.scheduleDates[i]);
    }
    return null;
  }

  onScheduleDateChange(index: number, date: any): void {
    if (date) {
      // MomentDateAdapter使用のため date は Moment オブジェクト
      this.form.schedules[index].eventDate = date.format('YYYY-MM-DD');
      this.scheduleDates[index] = date.toDate();
    } else {
      this.form.schedules[index].eventDate = '';
      this.scheduleDates[index] = null;
    }
  }

  formatScheduleDate(index: number): string {
    const d = this.scheduleDates[index];
    if (!d) return '';
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  }

  calcDuration(schedule: EventSchedule): string {
    if (!schedule.startTime || !schedule.endTime) return '';
    const [sh, sm] = schedule.startTime.split(':').map(Number);
    const [eh, em] = schedule.endTime.split(':').map(Number);
    const totalMin = (eh * 60 + em) - (sh * 60 + sm);
    if (totalMin <= 0) return '';
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0 && m > 0) return `${h}時間${m}分`;
    if (h > 0) return `${h}時間`;
    return `${m}分`;
  }

  // ===== Vendor =====

  getVendorCostLabel(cost: any): string {
    const n = Number(cost);
    if (cost === null || cost === undefined || cost === '') return '';
    if (n >= 5_010_000) return '501万円以上';
    if (n >= 4_010_000) return '401万円〜500万円';
    if (n >= 3_010_000) return '301万円〜400万円';
    if (n >= 2_010_000) return '201万円〜300万円';
    if (n >= 1_010_000) return '101万円〜200万円';
    if (n >= 510_000)   return '51万円〜100万円';
    if (n >= 410_000)   return '41万円〜50万円';
    if (n >= 310_000)   return '31万円〜40万円';
    if (n >= 210_000)   return '21万円〜30万円';
    if (n >= 110_000)   return '11万円〜20万円';
    if (n >= 60_000)    return '6万円〜10万円';
    if (n >= 10_000)    return '1万円〜5万円';
    else return '1万円未満'
  }

  addVendor(): void {
    if (this.form.vendors.length < 3) {
      this.form.vendors.push(this.createEmptyVendor());
    }
  }

  removeVendor(index: number): void {
    this.form.vendors.splice(index, 1);
  }

  // ===== Input sanitize =====

  onEstimatedParticipantsInput(event: Event): void {
    this.form.estimatedParticipants = this.sanitizeIntegerInput(event, 5);
  }

  onEstimatedTotalCostInput(event: Event): void {
    this.form.estimatedTotalCost = this.sanitizeIntegerInput(event, 10);
  }

  onSubsidyAmountInput(event: Event): void {
    this.form.subsidyAmount = this.sanitizeIntegerInput(event, 10);
  }

  onSpecificCostInput(event: Event, index: number): void {
    this.form.vendors[index].specificCost = this.sanitizeIntegerInput(event, 10);
  }

  private sanitizeIntegerInput(event: Event, maxLength: number): number | null {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^0-9]/g, '').slice(0, maxLength);
    input.value = cleaned;
    return cleaned ? parseInt(cleaned, 10) : null;
  }

  // ===== File upload =====

  onFileUpload(fileCategory: 1 | 2): void {
    if (this.uploadingCategory !== 0 || this.attachments[fileCategory].length >= 3) return;
    const input = fileCategory === 1 ? this.fileInput1 : this.fileInput2;
    input.nativeElement.value = '';
    input.nativeElement.click();
  }

  onFileSelected(event: Event, fileCategory: 1 | 2): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.allowedFileExtensions.includes(ext)) {
      this.toastUtil.showErrorToast('', Constant.msgErrorInvalidFile, Constant.toastShowMiliSecErr);
      return;
    }
    if (file.size > this.maxAttachmentSize) {
      this.toastUtil.showErrorToast('', Constant.msgErrorLargeFile, Constant.toastShowMiliSecErr);
      return;
    }
    this.uploadingCategory = fileCategory;

    const s3Config = {
      bucket: environment.amplify.Storage['myeventAttachment'].bucket,
      region: environment.amplify.Storage['myeventAttachment'].region,
      level: environment.amplify.Storage['myeventAttachment'].level as 'private' | 'public' | 'protected',
      contentType: file.type,
    };

    Auth.currentUserCredentials()
      .then(credentials => {
        const fileKey = Date.now().toString() + '.' + ext;
        return Storage.put(fileKey, file, s3Config).then(result => ({
          filePath: credentials.identityId + '/' + (result as any)['key'],
          fileKey: (result as any)['key'],
        }));
      })
      .then(({ filePath, fileKey }) => {
        const s3GetConfig = {
          bucket: environment.amplify.Storage['myeventAttachment'].bucket,
          region: environment.amplify.Storage['myeventAttachment'].region,
          level: environment.amplify.Storage['myeventAttachment'].level as 'private' | 'public' | 'protected',
          expires: 3600,
        };
        return Storage.get(fileKey, s3GetConfig).then((downloadUrl: any) => {
          this.attachments[fileCategory].push({ fileName: file.name, filePath, fileKey, downloadUrl });
        });
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        this.toastUtil.showErrorToast('', Constant.msgErrorUploadFile, Constant.toastShowMiliSecErr);
      })
      .finally(() => {
        this.uploadingCategory = 0;
      });
  }

  removeAttachment(fileCategory: 1 | 2, index: number): void {
    this.attachments[fileCategory].splice(index, 1);
  }

  onDownloadAttachment(att: AttachmentItem): void {
    if (!att.downloadUrl) return;
    this.http.get(att.downloadUrl, { responseType: 'arraybuffer' }).subscribe({
      next: data => {
        const blob = new Blob([data], { type: Constant.fileFormatDownload });
        saveAs(blob, att.fileName);
      },
      error: err => {
        this.toastUtil.showErrorToast('', Constant.msgFileDownloadError, Constant.toastShowMiliSec);
        this.commonUtil.debug().log(err);
      },
    });
  }


  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // ===== Org dialog =====

  private getDefaultSelectedNodes(): any[] {
    return this.myAccountOrgs.map(org => ({
      ...org,
      // 大分類レベルの所属は id=null で来るため、仮想ルートid(=master_type 1〜4)を割り当てる
      // （null のままだとカバー率集計で大分類が欠落し、配下ノードが誤計上されるため）
      id: org.id ?? org.master_type,
      path: org.master_type === 4
        ? `専門部 ${org.path}`.trim()
        : org.level === 0
          ? org.path
          : `${this.getMasterTypeName(org.master_type)} ${org.path}`
    }));
  }

  private refreshMasterData(): Promise<void> {
    return Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      return this.loadOfficialPositions(options);
    });
  }

  onOpenOrgDialog(type: 'organizer' | 'target'): void {
    if (!this.commonUtil.checkOnline()) return;

    // 別タブでマスタが追加・編集された場合に備え、最新のマスタ情報を取得する。
    // ダイアログ表示を待たせないよう、開くのと並行して裏で取得し、確定時に完了を待つ。
    // （追加マスタの反映漏れ・人数変更のカバー率反映のため）
    const masterRefresh = this.refreshMasterData().catch(() => {
      // 取得に失敗した場合は既存のマスタ情報で続行する
    });

    const selected =
      type === 'organizer'
        ? (!this.organizerOrgDetailNeverSet
            ? this.organizerSelectedNodes
            : this.getDefaultSelectedNodes())
        : (!this.targetOrgDetailNeverSet
            ? this.targetSelectedNodes
            : this.getDefaultSelectedNodes());

    const dialogRef = this.dialog.open(ManagerEditDialogComponent, {
      data: { selected, showClearAll: true, showSenmonbu: true, isTargetOrg: type === 'target', title: type === 'target' ? '対象組織' : '開催者所属組織' },
      width: Constant.managerEditDialogWidth,
      // 個人設定の担当選択ダイアログと同じく、画面が狭いときは画面に収める
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: ['manager-edit-dialog-large-font', 'responsive-dialog'],
      height: '664px',
      autoFocus: false,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result == null || result === '') return;

      // カバー率再計算の前に、裏で走らせたマスタ再取得の完了を待つ
      await masterRefresh;

      const ids: number[] = result.map((n: any) => n.id);
      const nodes = this.buildSelectedNodesFromIds(ids);

      if (type === 'organizer') {
        this.organizerSelectedNodes = nodes;
        this.form.organizerOrgDetail = ids;
        this.form.organizerOrgDetailText = nodes.map((n: any) => n.path).join('\n');
        this.organizerOrgDetailNeverSet = false;
      } else {
        this.targetSelectedNodes = nodes;
        this.form.targetOrgDetail = ids;
        this.form.targetOrgDetailText = nodes.map((n: any) => n.path).join('\n');
        this.targetOrgDetailNeverSet = false;
      }
    });
  }

  // ===== Save / Cancel =====

  onSave(): void {
    if (!this.commonUtil.checkOnline()) return;

    if (this.form.status !== 'draft') {
      const errors = this.validateRequired();
      console.log(errors)
      if (errors.length > 0) {
        this.toastUtil.showErrorToast('', '必須項目が未入力です。', Constant.toastShowMiliSecErr);
        return;
      }
    }

    this.isSaving = true;
    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        options['body'] = this.buildPayload();
        //console.log(options)
        return this.commonUtil.apiPut('/myevent/edit/' + this.eventId, options);
      })
      .then(res => {
        if (res.status === Constant.OK) {
          this.toastUtil.showInformationToast('', '保存しました。', Constant.toastShowMiliSec);
        }
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        } else {
          this.toastUtil.showErrorToast('', Constant.msgSaveFailedRetry, Constant.toastShowMiliSecErr);
        }
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  onCancel(): void {
    const dialogRef = this.dialog.open(GeneralYesNoDialogComponent, {
      width: Constant.dialogWidth,
      autoFocus: false,
      data: {
        msg: Constant.msgConfirmBack
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancelled.emit();
      }
    });


  }

  isAllUnionMember(detailId: number): boolean {
    return detailId === 200;
  }

  isEmploymentType(detailId: number): boolean {
    return detailId === 201 || detailId === 202;
  }

  hasAllUnionMemberSelected(): boolean {
    return this.isMultiSelected(this.form.participantDetails, 200);
  }

  onParticipantDetailChange(): void {
    if (this.hasAllUnionMemberSelected()) {
      this.form.participantDetails =
        this.form.participantDetails.filter(
          id => id !== 201 && id !== 202
        );
    }
  }

  toggleParticipantDetail(id: number): void {
    this.toggleMultiSelect(this.form.participantDetails, id);

    // 組合員全体を選択した場合
    if (
      id === 200 &&
      this.form.participantDetails.includes(200)
    ) {
      this.form.participantDetails =
        this.form.participantDetails.filter(
          x => x !== 201 && x !== 202
        );
    }
  }

  /** 年代（attr_generation）の「全年代」のマスターID */
  private get generationAllId(): number | null {
    return this.getMasterItems('attr_generation').find(m => m.name === '全年代')?.id ?? null;
  }

  toggleGenerationAttribute(id: number): void {
    if (this.form.participantAttributes === null) {
      this.form.participantAttributes = [];
    }

    this.toggleMultiSelect(this.form.participantAttributes, id);

    const allId = this.generationAllId;
    if (allId === null) {
      return;
    }

    if (id === allId) {
      // 「全年代」を選択した場合は他の年代のチェックを外す
      if (this.form.participantAttributes.includes(allId)) {
        const generationIds = this.getMasterItems('attr_generation').map(m => m.id);
        this.form.participantAttributes =
          this.form.participantAttributes.filter(
            x => x === allId || !generationIds.includes(x)
          );
      }
      return;
    }

    // 「全年代」以外を選択した場合は「全年代」のチェックを外す
    if (this.form.participantAttributes.includes(id)) {
      this.form.participantAttributes =
        this.form.participantAttributes.filter(x => x !== allId);
    }
  }

  /** 「全年代」を選択中は、他の年代を選択不可にする */
  isGenerationDisabled(id: number): boolean {
    const allId = this.generationAllId;
    if (allId === null || id === allId) return false;
    return (this.form.participantAttributes ?? []).includes(allId);
  }

  // ===== Private =====

  private loadData(): void {
    this.showSpinner = true;
    Auth.currentSession()
      .then(session => {
        const options = this.auth.createApiHeader(session);
        return Promise.all([
          this.loadMasterData(options),
          this.loadFormData(options),
          this.loadMyAccount(options),
          this.loadOfficialPositions(options),
          this.loadAuthDescription()
        ]);
      })
      .then(() => {
        if (this.form.categoryId !== null) {
          const found = this.getMasterItems('category_major').find(m => m.id === this.form.categoryId);
          if (found) this.categoryMajorName = found.name;
        } else {
          const found = this.getMasterItems('category_major').find(m => m.name === this.categoryMajorName);
          if (found) this.form.categoryId = found.id;
        }
        if (this.form.publicScope === null && this.form.categoryId !== null) {
          this.setDefaultPublicScope();
        }
        this.setDefaultImprovementIndicators();
        this.setDefaultOrganizerOrgs();
        if (this.form.organizerOrgDetail.length > 0) {
          this.organizerSelectedNodes = this.buildSelectedNodesFromIds(this.form.organizerOrgDetail);
        }
        if (this.form.targetOrgDetail.length > 0) {
          this.targetSelectedNodes = this.buildSelectedNodesFromIds(this.form.targetOrgDetail);
        }
        this.initScheduleDates();
        this.showSpinner = false;
      })
      .catch(err => {
        this.commonUtil.debug().log(err);
        if (err.message === Constant.msgLoginFailed) {
          this.commonUtil.showReloadDialog(this.auth);
        } else {
          this.showSpinner = false;
          this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
        }
      });
  }

  private setDefaultImprovementIndicators(): void {
    //const stateItems = this.getMasterItems('improvement_indicator_state');
    //if ( this.form.improvementIndicatorState.every(v => v === null) && stateItems.length > 0 ) {
    //  this.form.improvementIndicatorState[0] = stateItems[0].id;
    //}
    const outcomeItems = this.getMasterItems('improvement_indicator_outcome');
    if (this.form.improvementIndicatorOutcome === null && outcomeItems.length > 0) {
      this.form.improvementIndicatorOutcome = outcomeItems[0].id;
    }
    const participantItems = this.getMasterItems('participant_category');
    if (this.form.participantCategory === null && participantItems.length > 0) {
      this.form.participantCategory = participantItems[0].id;
    }
    if (this.form.participantAttributes == null) {
      // 全年代
      const generationAll = this.getMasterItems('attr_generation')
        .find(m => m.name === '全年代');

      // 男性
      const male = this.getMasterItems('attr_gender')
        .find(m => m.name === '男性');

      // 女性
      const female = this.getMasterItems('attr_gender')
        .find(m => m.name === '女性');

      this.form.participantAttributes = [
        ...(generationAll ? [generationAll.id] : []),
        ...(male ? [male.id] : []),
        ...(female ? [female.id] : []),
      ];
    }
    const implItems = this.getMasterItems('implementation_method');
    if (this.form.implementationMethod === null && implItems.length > 0) {
      this.form.implementationMethod = implItems[0].id;
    }
    const organizerUnitItems = this.getMasterItems('organizer_org_unit');
    if (this.form.organizerUnit === null && organizerUnitItems.length > 0) {
      this.form.organizerUnit = organizerUnitItems[1].id;
    }
    const targetUnitItems = this.getMasterItems('organizer_org_unit');
    if (this.form.targetUnit === null && targetUnitItems.length > 0) {
      this.form.targetUnit = targetUnitItems[0].id;
    }
    if (this.form.companyOtherCollaboration === null && this.form.categoryId !== 10) {
      this.form.companyOtherCollaboration = false;
    }
    if (this.form.companyCollaboration === null && this.form.categoryId !== 10) {
      this.form.companyCollaboration = true;
    }
    const venueItems = this.getMasterItems('venue');
    if (this.form.venue === null && venueItems.length > 0) {
      this.form.venue = venueItems[0].id;
    }
    const effectivenessItems = this.getMasterItems('effectiveness_measurement');
    if (this.form.effectivenessMeasurement === null && effectivenessItems.length > 0 && this.form.categoryId !== 10) {
      this.form.effectivenessMeasurement = effectivenessItems[0].id;
    }
    if (this.form.categoryId !== 10) {
      const holdingItems = this.getMasterItems('holding_method');
      if (this.form.holdingMethod === null && holdingItems.length > 0) {
        this.form.holdingMethod = holdingItems[0].id;
      }
      const educationItems = this.getMasterItems('education_format');
      if (
        this.form.categoryId !== 2 &&
        (!this.form.educationFormat || this.form.educationFormat.length === 0) &&
        educationItems.length > 0
      ) {
        this.form.educationFormat = [educationItems[0].id];
      }

      const surveyItems = this.getMasterItems('survey_method');
      if (this.form.surveyMethod === null && surveyItems.length > 0) {
        this.form.surveyMethod = surveyItems[0].id;
      }
    }
  }

  private setDefaultPublicScope(): void {
    this.form.publicScope = this.form.categoryId! <= 3 || this.form.categoryId! === 5 || this.form.categoryId! === 6 || this.form.categoryId! === 10 ? 286 : 289;
  }

  private initScheduleDates(): void {
    this.scheduleDates = this.form.schedules.map(s => {
      if (!s.eventDate) return null;
      const [y, m, d] = s.eventDate.split('-').map(Number);
      return new Date(y, m - 1, d);
    });
  }

  private loadOfficialPositions(options: object): Promise<void> {
    return this.commonUtil
      .apiGet('/master', options)
      .then(res => {
        const master = res.data?.master ?? {};
        const positions: any[] = master[5] ?? [];
        this.officialPositionItems = positions.map(p => ({
          id: p.id,
          name: p.name,
          parentId: null,
          attribute: null,
          sortOrder: p.sort_order,
          authority: p.authority,
        }));
        this.orgMasterData = master;
        this.masterCountData = res.data.master_count;
      });
  }

  private loadMyAccount(options: object): Promise<void> {
    return this.commonUtil
      .apiGet('/myaccount', options)
      .then(res => {
        const orgs = res.data?.organizations ?? [];
        const concurrent = (res.data?.concurrent_organizations ?? []).map((org: any) => ({
          ...org,
          master_type: 4,
        }));
        this.myAccountOrgs = [...orgs, ...concurrent];
      });
  }

  private setDefaultOrganizerOrgs(): void {
    const nodes = this.myAccountOrgs.map(org => ({
      ...org,
      // 大分類レベルの所属は id=null で来るため、仮想ルートid(=master_type 1〜4)を割り当てる
      id: org.id ?? org.master_type,
      path: org.master_type === 4
        ? `専門部 ${org.path}`.trim()
        : org.level === 0
          ? org.path
          : `${this.getMasterTypeName(org.master_type)} ${org.path}`
    }));

    if (this.form.organizerOrgDetail.length === 0 && this.organizerOrgDetailNeverSet) {
      this.organizerSelectedNodes = nodes;
      this.form.organizerOrgDetail = nodes.map(n => n.id);
      this.form.organizerOrgDetailText = nodes.map(n => n.path).join('\n');
    }

    if (this.form.targetOrgDetail.length === 0 && this.targetOrgDetailNeverSet) {
      this.targetSelectedNodes = nodes;
      this.form.targetOrgDetail = nodes.map(n => n.id);
      this.form.targetOrgDetailText = nodes.map(n => n.path).join('\n');
    }
  }

  private buildSelectedNodesFromIds(ids: number[]): any[] {
    if (!ids || ids.length === 0) return [];

    const idSet = new Set(ids);
    const result: any[] = [];

    // #skipを透過して最初の非skipレベルの人数を合計する
    // - #skipノードは透過して子を再帰探索
    // - 非skipノードに到達したらmember_countを取得し、それより下は見ない
    const sumChushinCount = (nodes: any[]): number =>
      nodes.reduce((sum: number, n: any) => {
        if (n.name?.includes('#skip')) {
          return sum + sumChushinCount(n.children ?? []);
        }
        return sum + (n.member_count ?? 0);
      }, 0);

    // 大分類（本部・支部・エリア・専門部）単体の人数を求める
    // - マスタ設定で人数が入力されていればそれを採用（＝下位は考慮しない）
    // - 人数がブランク（null）の場合のみ、中分類以下を合計する
    const daibunruiCount = (masterType: number): number => {
      const headcount = this.masterCountData?.[masterType]?.member_count;
      return headcount != null
        ? headcount
        : sumChushinCount(this.orgMasterData[String(masterType)] ?? []);
    };

    // 全体（先頭固定）: 各大分類の値を合計する
    if (idSet.has(-1)) {
      const zenbuCount = [1, 2, 3, 4].reduce((total, key) => total + daibunruiCount(key), 0);
      result.push({ id: -1, name: '全体', path: '全体', master_type: 0, org_count: zenbuCount, orgCountForCoverage: zenbuCount });
    }

    type Entry = { id: number; name: string; level: number; memberCount: number | null; masterType: number; ancestorIds: number[]; isSkip: boolean; isVirtualRoot: boolean };
    const allEntries: Entry[] = [];

    const virtualRoots = [
      { id: 1, name: '本部',  masterType: 1, key: '1' },
      { id: 2, name: '支部',  masterType: 2, key: '2' },
      { id: 3, name: 'エリア', masterType: 3, key: '3' },
      { id: 4, name: '専門部', masterType: 4, key: '4' },
    ];

    for (const vr of virtualRoots) {
      allEntries.push({ id: vr.id, name: vr.name, level: 0, memberCount: null, masterType: vr.masterType, ancestorIds: [], isSkip: false, isVirtualRoot: true });

      const traverse = (nodes: any[], ancestorIds: number[]) => {
        for (const n of nodes) {
          const isSkip = n.name?.includes('#skip');
          allEntries.push({ id: n.id, name: n.name, level: n.level, memberCount: n.member_count ?? null, masterType: vr.masterType, ancestorIds: [...ancestorIds], isSkip, isVirtualRoot: false });
          if (n.children) traverse(n.children, [...ancestorIds, n.id]);
        }
      };
      traverse(this.orgMasterData[vr.key] ?? [], [vr.id]);
    }

    const selectedEntries = allEntries.filter(e => idSet.has(e.id));
    const selectedIdSet = new Set(selectedEntries.map(e => e.id));

    for (const e of selectedEntries) {
      // 大分類（仮想ルート）が選択済みの場合、その下の中分類は二重カウントしない
      const hasCheckedParentWithOrgCount = e.ancestorIds.some(aid => {
        if (!selectedIdSet.has(aid)) return false;
        const anc = allEntries.find(a => a.id === aid);
        return anc != null && (anc.memberCount != null || anc.isVirtualRoot);
      });

      // その組織「単体」の人数
      // - 大分類: 人数優先（ブランク時のみ中分類以下を合計）
      // - 中分類以下: 自身の member_count
      const ownCount = e.isVirtualRoot
        ? daibunruiCount(e.masterType)
        : (e.memberCount || 0);

      // 上位のマスタが選択済みの場合は下位を二重計上しない
      const orgCountForCoverage = hasCheckedParentWithOrgCount ? 0 : ownCount;

      const pathParts: string[] = [];
      for (const aid of e.ancestorIds) {
        const anc = allEntries.find(a => a.id === aid);
        if (anc && !anc.isSkip) pathParts.push(anc.name);
      }
      if (!e.isSkip) pathParts.push(e.name);

      result.push({ id: e.id, name: e.name, level: e.level, org_count: ownCount, master_type: e.masterType, path: pathParts.join(' '), orgCountForCoverage });
    }

    // 他組織（末尾固定）
    if (idSet.has(-2)) result.push({ id: -2, name: '他組織', path: '他組織', master_type: 0, org_count: null, orgCountForCoverage: 0 });

    return result;
  }

  private getMasterTypeName(masterType: number): string {
    const map: { [key: number]: string } = { 1: '本部', 2: '支部', 3: 'エリア', 4: '専門部' };
    return map[masterType] ?? '';
  }

  // 大分類を id=null で保存した旧データを、並列するテキスト行の大分類名から
  // 仮想ルートid(1〜4)へ復元する（null のままだとカバー率集計で大分類が欠落するため）
  private normalizeDaibunruiIds(ids: any[], text: string): any[] {
    if (!Array.isArray(ids)) return ids;
    const nameToType: { [k: string]: number } = { '本部': 1, '支部': 2, 'エリア': 3, '専門部': 4 };
    const lines = (text ?? '').split('\n');
    return ids.map((id, i) => {
      if (id != null) return id;
      const line = (lines[i] ?? '').trim();
      return nameToType[line] ?? id;
    });
  }

  private loadMasterData(options: object): Promise<void> {
    return this.commonUtil
      .apiGet('/myevent/master', options)
      .then(res => {
        this.masterData = res.data;
      });
  }

  private loadFormData(options: object): Promise<void> {
    return this.commonUtil
      .apiGet('/myevent/edit/' + this.eventId, options)
      .then(res => {
        const d = Array.isArray(res.data) ? res.data[0] : res.data;

        this.form.title        = d.title   ?? '';
        this.form.status       = d.status  ?? 'draft';
        this.form.categoryId   = d.categoryId ?? null;
        this.form.totalMembers = d.totalMembers ?? null;

        this.form.categorySub    = [d.categorySub1 ?? null, d.categorySub2 ?? null, d.categorySub3 ?? null];
        this.form.publicScope    = d.publicScope ?? null;
        this.form.publicScopeRoles = d.publicScopeRoles ?? [];
        this.form.issueAwareness = [d.issueAwareness1 ?? null, d.issueAwareness2 ?? null, d.issueAwareness3 ?? null];
        this.form.issueBasis                  = d.issueBasis                  ?? null;
        this.form.basisTargetDivision         = d.basisTargetDivision         ?? null;
        this.form.basisTargetPeriod           = d.basisTargetPeriod           ?? null;
        this.form.improvementIndicatorState   = [d.improvementIndicatorState1 ?? null, d.improvementIndicatorState2 ?? null, d.improvementIndicatorState3 ?? null];
        this.form.improvementIndicatorOutcome = d.improvementIndicatorOutcome ?? null;
        this.form.purpose     = [d.purpose1 ?? null, d.purpose2 ?? null, d.purpose3 ?? null];
        this.form.summary     = d.summary ?? '';
        this.form.socialTheme = [d.socialTheme1 ?? null, d.socialTheme2 ?? null, d.socialTheme3 ?? null];

        this.form.organizerUnit          = d.organizerUnit          ?? null;
        this.organizerOrgDetailNeverSet  = d.organizerOrgDetail === null;
        this.form.organizerOrgDetail     = this.normalizeDaibunruiIds(d.organizerOrgDetail ?? [], d.organizerOrgDetailText ?? '');
        this.form.organizerOrgDetailText = d.organizerOrgDetailText ?? '';
        this.form.targetUnit             = d.targetUnit             ?? null;
        this.targetOrgDetailNeverSet     = d.targetOrgDetail === null;
        this.form.targetOrgDetail        = this.normalizeDaibunruiIds(d.targetOrgDetail ?? [], d.targetOrgDetailText ?? '');
        this.form.targetOrgDetailText    = d.targetOrgDetailText    ?? '';
        this.form.participantCategory    = d.participantCategory    ?? null;
        this.form.participantDetails     = d.participantDetails     ?? [];
        this.participantDetailPositions  = (d.participantDetailPositions ?? []).map((p: any) => ({
          id: p.id,
          name: p.name,
          parentId: null,
          attribute: null,
          sortOrder: p.sortOrder,
        }));
        this.form.companyOtherCollaboration   = d.companyOtherCollaboration   ?? null;
        this.form.companyCollaboration   = d.companyCollaboration   ?? null;
        this.form.participantAttributes = d.participantAttributes ?? null;

        this.form.implementationMethod    = d.implementationMethod    ?? null;
        this.form.holdingMethod           = d.holdingMethod           ?? null;
        this.form.educationFormat         = d.educationFormat         ?? [];
        this.form.surveyMethod            = d.surveyMethod            ?? null;
        this.form.announcementMethod = [d.announcementMethod1 ?? null, d.announcementMethod2 ?? null, d.announcementMethod3 ?? null];
        this.form.recruitmentMethod  = [d.recruitmentMethod1 ?? null, d.recruitmentMethod2 ?? null, d.recruitmentMethod3 ?? null];

        this.form.venue                    = d.venue                    ?? null;
        this.form.venueDetail              = d.venueDetail              ?? '';
        this.form.numberOfDays             = d.numberOfDays             ?? '1';
        this.form.estimatedParticipants    = d.estimatedParticipants    ?? null;
        this.form.estimatedTotalCost       = d.estimatedTotalCost       ?? null;
        this.form.subsidyAmount            = d.subsidyAmount            ?? null;
        this.form.vendors                  = d.vendors?.length > 0 ? d.vendors : [this.createEmptyVendor()];
        this.form.implementationChallenges = d.implementationChallenges ?? '';
        this.form.countermeasures          = d.countermeasures          ?? '';
        this.form.workplaceExpectedIssues  = d.workplaceExpectedIssues  ?? '';
        this.form.keyHearingItems          = d.keyHearingItems          ?? '';
        this.form.effectivenessMeasurement = d.effectivenessMeasurement ?? null;
        this.form.meetingFormats           = d.meetingFormats           ?? [];
        this.form.counterpartTypes         = d.counterpartTypes         ?? [];
        this.form.memo                     = d.memo                     ?? '';
        this.form.schedules = d.schedules?.length > 0
          ? d.schedules.map((s: any) => ({
              dayIndex:  s.dayIndex,
              eventDate: s.eventDate ?? '',
              startTime: s.startTime ? (s.startTime as string).substring(0, 5) : '',
              endTime:   s.endTime   ? (s.endTime   as string).substring(0, 5) : '',
            }))
          : [{ dayIndex: 1, eventDate: '', startTime: '', endTime: '' }];

        this.attachments[1] = (d.attachments ?? [])
          .filter((a: any) => a.fileCategory === 1)
          .map((a: any) => ({ fileName: a.fileName, filePath: a.filePath, fileKey: '', downloadUrl: a.downloadUrl }));
        this.attachments[2] = (d.attachments ?? [])
          .filter((a: any) => a.fileCategory === 2)
          .map((a: any) => ({ fileName: a.fileName, filePath: a.filePath, fileKey: '', downloadUrl: a.downloadUrl }));
      });
  }

  private validateRequired(): string[] {
    const errors: string[] = [];

    if (!this.form.title) errors.push('タイトル');
    if (!this.form.categoryId) errors.push('カテゴリー');

    // 中分類 ※
    if (!this.form.categorySub?.some(v => v != null)) {
      errors.push('中分類');
    }

    if (!this.form.publicScope) errors.push('公開範囲');

    // 課題認識
    // if (this.showIssueSection && this.form.issueAwareness.every(v => v === null)) {
    //   errors.push('課題認識');
    // }

    // 改善したい指標（状態）
    if (this.showIssueSection &&
        this.form.improvementIndicatorState?.every(v => v === null)) {
      errors.push('改善したい指標（状態）');
    }

    // 目的
    if (this.showIssueSection && this.form.purpose?.every(v => v === null)) {
      errors.push('目的');
    }

    if (!this.form.summary) errors.push('概要');

    // 社会テーマ
    if (this.showSocialTheme &&
        !this.form.socialTheme?.some(v => v != null)) {
      errors.push('社会テーマ');
    }

    // 開催者所属
    if (!this.form.organizerUnit) {
      errors.push('開催者所属');
    }

    if (!this.form.organizerOrgDetailText) {
      errors.push('開催者所属（詳細）');
    }

    if (this.showCompanyOtherCollaboration && this.form.categoryId != 10 &&
        this.form.companyOtherCollaboration === null) {
      errors.push('他労組共同開催');
    }

    if (this.showCompanyCollaboration && this.form.categoryId != 10 &&
        this.form.companyCollaboration === null) {
      errors.push('会社協働・協賛');
    }

    // 対象組織
    if (this.showTargetUnit && !this.form.targetUnit) {
      errors.push('対象組織（単位）');
    }

    if (this.showTargetOrg && !this.form.targetOrgDetailText) {
      errors.push('対象組織（詳細）');
    }

    // 対象/参加者
    if (this.showTargetOrg && !this.form.participantCategory) {
      errors.push('対象/参加者');
    }

    // 対象/参加者詳細
    if (
      this.showTargetOrg &&
      this.form.participantCategory !== null &&
      this.form.participantCategory !== 199 &&
      !this.form.participantDetails?.some(v => v != null)
    ) {
      errors.push('対象/参加者（詳細）');
    }

    if (!this.form.implementationMethod) {
      errors.push('実施方法');
    }

    if (this.showHoldingMethod && !this.form.holdingMethod && this.form.categoryId != 10) {
      errors.push('開催方法');
    }

    if (
      this.showEducationFormat &&
      this.form.categoryId == 2 &&
      (
        !this.form.educationFormat ||
        this.form.educationFormat.length === 0 ||
        this.form.educationFormat.every(v => v === null)
      )
    ) {
      errors.push('教育形式');
    }

    if (this.showSurveyMethod && !this.form.surveyMethod && this.form.categoryId != 10) {
      errors.push('調査方法');
    }

    if (this.showAnnouncementMethod &&
        !this.form.announcementMethod?.some(v => v != null)) {
      errors.push('案内方法');
    }

    if (this.showRecruitmentMethod &&
        !this.form.recruitmentMethod?.some(v => v != null)) {
      errors.push('募集方法');
    }

    if (this.showVenue && !this.form.venue) {
      errors.push('会場');
    }

    // 開催日程

    if (!this.form.numberOfDays) {
      errors.push('開催日数');
    }

    for (const row of this.scheduleRows) {
      const schedule = this.form.schedules?.[row.index];
      const date = this.scheduleDates?.[row.index];

      if (
        !date ||
        !schedule?.startTime ||
        !schedule?.endTime ||
        schedule.startTime > schedule.endTime
      ) {
        errors.push('開催日程');
        break;
      }
    }

    if (this.showEstimatedParticipants &&
        this.form.estimatedParticipants == null) {
      errors.push('想定参加人数');
    }

    if (this.showCostSection &&
        this.form.categoryId !== 10 &&
        this.form.estimatedTotalCost == null) {
      errors.push('想定費用総額');
    }

    if (this.showSubsidyAndVendors &&
        this.form.categoryId !== 10 &&
        this.form.subsidyAmount == null) {
      errors.push('補助金額');
    }

    if (this.showWorkplaceSection &&
      this.form.workplaceExpectedIssues.length == 0) {
    errors.push('該当職場の想定議題');
    }

    if (this.showWorkplaceSection &&
      this.form.keyHearingItems.length == 0) {
    errors.push('重点ヒアリング項目');
    }

    if (this.showEffectiveness &&
        this.form.effectivenessMeasurement == null
        && this.form.categoryId != 10) {
      errors.push('効果測定');
    }

    if (this.showMeetingSection &&
        !this.form.meetingFormats?.some(v => v != null)) {
      errors.push('会議体・対応形式');
    }

    if (this.showMeetingSection &&
        !this.form.counterpartTypes?.some(v => v != null)) {
      errors.push('主な相手区分');
    }

    return errors;
  }

  private buildPayload(): object {
    return {
      title: this.form.title,
      categoryId: this.form.categoryId,
      status: this.form.status,
      categorySub1: this.form.categorySub[0],
      categorySub2: this.form.categorySub[1],
      categorySub3: this.form.categorySub[2],
      publicScope: this.form.publicScope,
      issueAwareness1: this.showIssueSection ? this.form.issueAwareness[0] : null,
      issueAwareness2: this.showIssueSection ? this.form.issueAwareness[1] : null,
      issueAwareness3: this.showIssueSection ? this.form.issueAwareness[2] : null,
      issueBasis: this.showIssueSection ? this.form.issueBasis : null,
      basisTargetDivision: this.showIssueSection ? this.form.basisTargetDivision : null,
      basisTargetPeriod: this.showIssueSection ? this.form.basisTargetPeriod : null,
      improvementIndicatorState1: this.showIssueSection ? this.form.improvementIndicatorState[0] : null,
      improvementIndicatorState2: this.showIssueSection ? this.form.improvementIndicatorState[1] : null,
      improvementIndicatorState3: this.showIssueSection ? this.form.improvementIndicatorState[2] : null,
      improvementIndicatorOutcome: this.showImprovementOutcome ? this.form.improvementIndicatorOutcome : null,
      purpose1: this.form.purpose[0],
      purpose2: this.form.purpose[1],
      purpose3: this.form.purpose[2],
      summary: this.form.summary,
      socialTheme1: this.showSocialTheme ? this.form.socialTheme[0] : null,
      socialTheme2: this.showSocialTheme ? this.form.socialTheme[1] : null,
      socialTheme3: this.showSocialTheme ? this.form.socialTheme[2] : null,
      organizerUnit: this.form.organizerUnit,
      organizerOrgDetail: this.form.organizerOrgDetail,
      organizerOrgDetailText: this.form.organizerOrgDetailText,
      targetUnit: this.showTargetUnit ? this.form.targetUnit : null,
      targetOrgDetail: this.showTargetOrg ? this.form.targetOrgDetail : [],
      targetOrgDetailText: this.showTargetOrg ? this.form.targetOrgDetailText : '',
      participantCategory: !this.isLaborNegotiation ? this.form.participantCategory : null,
      participantDetails: !this.isLaborNegotiation ? this.form.participantDetails : [],
      companyOtherCollaboration: this.showCompanyOtherCollaboration ? this.form.companyOtherCollaboration : null,
      companyCollaboration: this.showCompanyCollaboration ? this.form.companyCollaboration : null,
      attrGeneration: this.showAttr
      ? (this.form.participantAttributes ?? []).filter(id =>
          this.getMasterItems('attr_generation').some(m => m.id === id)
        )
      : [],
      attrGender: this.showAttr
      ? (this.form.participantAttributes ?? []).filter(id =>
          this.getMasterItems('attr_gender').some(m => m.id === id)
        )
      : [],
      implementationMethod: this.form.implementationMethod,
      holdingMethod: this.showHoldingMethod ? this.form.holdingMethod : null,
      educationFormat: this.showEducationFormat ? this.form.educationFormat : null,
      surveyMethod: this.showSurveyMethod ? this.form.surveyMethod : null,
      announcementMethod1: this.showAnnouncementMethod ? this.form.announcementMethod[0] : null,
      announcementMethod2: this.showAnnouncementMethod ? this.form.announcementMethod[1] : null,
      announcementMethod3: this.showAnnouncementMethod ? this.form.announcementMethod[2] : null,
      recruitmentMethod1: this.showRecruitmentMethod ? this.form.recruitmentMethod[0] : null,
      recruitmentMethod2: this.showRecruitmentMethod ? this.form.recruitmentMethod[1] : null,
      recruitmentMethod3: this.showRecruitmentMethod ? this.form.recruitmentMethod[2] : null,
      venue: this.showVenue ? this.form.venue : null,
      venueDetail: this.showVenue ? (this.form.venueDetail || '').trim().substring(0, 50) : '',
      numberOfDays: this.form.numberOfDays,
      estimatedParticipants: this.showEstimatedParticipants ? this.form.estimatedParticipants : null,
      estimatedTotalCost: this.showCostSection ? this.form.estimatedTotalCost : null,
      subsidyAmount: this.showSubsidyAndVendors ? this.form.subsidyAmount : null,
      vendors: this.showSubsidyAndVendors ? this.form.vendors : [],
      implementationChallenges: this.form.implementationChallenges,
      countermeasures: this.form.countermeasures,
      workplaceExpectedIssues: this.showWorkplaceSection ? this.form.workplaceExpectedIssues : '',
      keyHearingItems: this.showWorkplaceSection ? this.form.keyHearingItems : '',
      effectivenessMeasurement: this.showEffectiveness ? this.form.effectivenessMeasurement : null,
      meetingFormats: this.showMeetingSection ? this.form.meetingFormats : [],
      counterpartTypes: this.showMeetingSection ? this.form.counterpartTypes : [],
      memo: this.form.memo,
      schedules: this.form.schedules,
      totalMembers: this.form.totalMembers,
      attachments: [
        ...this.attachments[1].map((a, i) => ({
          fileCategory: 1, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1,
        })),
        ...this.attachments[2].map((a, i) => ({
          fileCategory: 2, fileName: a.fileName, filePath: a.filePath, sortOrder: i + 1,
        })),
      ],
      coverageRate: this.coverageRateValue?.toString().trim() === '' ? null : this.coverageRateValue,
    };
  }

  private createEmptyForm(): EventPlanForm {
    return {
      title: '',
      categoryId: null,
      status: 'draft',
      categorySub: [null, null, null],
      publicScope: null,
      issueAwareness: [null, null, null],
      issueBasis: null,
      basisTargetDivision: null,
      basisTargetPeriod: null,
      improvementIndicatorState: [null, null, null],
      improvementIndicatorOutcome: null,
      purpose: [null, null, null],
      summary: '',
      socialTheme: [null, null, null],
      organizerUnit: null,
      organizerOrgDetail: [],
      organizerOrgDetailText: '',
      targetUnit: null,
      targetOrgDetail: [],
      targetOrgDetailText: '',
      participantCategory: null,
      participantDetails: [],
      companyOtherCollaboration: null,
      companyCollaboration: null,
      participantAttributes: null,
      implementationMethod: null,
      holdingMethod: null,
      educationFormat: [],
      surveyMethod: null,
      announcementMethod: [null, null, null],
      recruitmentMethod: [null, null, null],
      venue: null,
      venueDetail: '',
      numberOfDays: '1',
      estimatedParticipants: null,
      estimatedTotalCost: null,
      subsidyAmount: null,
      vendors: [this.createEmptyVendor()],
      implementationChallenges: '',
      countermeasures: '',
      workplaceExpectedIssues: '',
      keyHearingItems: '',
      effectivenessMeasurement: null,
      meetingFormats: [],
      counterpartTypes: [],
      memo: '',
      schedules: [{ dayIndex: 1, eventDate: '', startTime: '', endTime: '' }],
      totalMembers: null,
      lastUpdatedName: null,
      lastUpdatedOfficialPositionName: null,
      lastUpdatedAt: null,
    };
  }

  private createEmptyVendor(): EventVendor {
    return { companyName: '', contactPerson: '', contactInfo: '', workSummary: '', workDurationMinutes: null, specificCost: null };
  }

  private generateDurationOptions(): { value: number; label: string }[] {
    const opts: { value: number; label: string }[] = [];
    for (let m = 30; m <= 1440; m += 30) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      let label = '';
      if (h > 0 && min > 0) label = `${h}時間${min}分`;
      else if (h > 0) label = `${h}時間`;
      else label = `${min}分`;
      opts.push({ value: m, label });
    }
    return opts;
  }

  get isFormValid(): boolean {
    // タイトル
    if (!this.form.title?.trim()) return false;

    // カテゴリー
    if (!this.form.categoryId) return false;

    // 公開範囲
    if (this.form.publicScope == null) return false;

    // =========================
    // 課題認識セクション
    // =========================
    if (this.showIssueSection) {
      // 課題認識
      if (!this.form.issueAwareness?.length) return false;

      // 改善したい指標
      if (this.form.improvementIndicatorState == null) return false;

      // 企画の成果（表示されている場合のみ）
      if (
        this.showImprovementOutcome &&
        this.form.improvementIndicatorOutcome == null
      ) {
        return false;
      }

      // 目的
      if (!this.form.purpose?.length) return false;
    }

    // 概要
    if (!this.form.summary?.trim()) return false;

    // =========================
    // 社会テーマ
    // =========================
    if (this.showSocialTheme) {
      if (!this.form.socialTheme?.length) return false;
    }

    // =========================
    // 開催者所属組織
    // =========================

    // 単位（表示されている場合）
    if (this.form.organizerUnit == null) return false;

    // 詳細
    if (!this.form.organizerOrgDetailText?.trim()) return false;

    // =========================
    // 対象組織
    // =========================
    if (this.showTargetOrg) {
      // 単位
      if (this.showTargetUnit) {
        if (this.form.targetUnit == null) return false;
      }

      // 詳細
      if (!this.form.targetOrgDetailText?.trim()) return false;

      // 対象/参加者
      if (this.form.participantCategory == null) return false;
    }

    // =========================
    // 会社協働・協賛
    // =========================
    if (this.showCompanyCollaboration && this.form.categoryId != 10) {
      if (this.form.companyCollaboration == null) return false;
    }

    // =========================
    // 実施方法
    // =========================
    if (this.form.implementationMethod == null) return false;

    // =========================
    // 開催方法
    // =========================
    if (this.showHoldingMethod && this.form.categoryId == 1) {
      if (this.form.holdingMethod == null) return false;
    }

    // =========================
    // 教育形式
    // =========================
    if (this.showEducationFormat && this.form.categoryId == 2) {
      if (this.form.educationFormat == null) return false;
    }

    // =========================
    // 調査方法
    // =========================
    if (this.showSurveyMethod && this.form.categoryId != 10) {
      if (this.form.surveyMethod == null) return false;
    }

    // =========================
    // 案内方法
    // =========================
    if (this.showAnnouncementMethod) {
      if (!this.form.announcementMethod?.length) return false;
    }

    // =========================
    // 募集方法
    // =========================
    if (this.showRecruitmentMethod) {
      if (!this.form.recruitmentMethod?.length) return false;
    }

    // =========================
    // 会場
    // =========================
    if (this.showVenue) {
      if (this.form.venue == null) return false;
    }

    // =========================
    // 開催日程
    // =========================

    // 開催日数
    if (!this.form.numberOfDays) return false;

    // スケジュール
    if (!this.form.schedules?.length) return false;

    for (const schedule of this.form.schedules) {
      if (!schedule.eventDate) return false;

      if (!schedule.startTime) return false;

      if (!schedule.endTime) return false;
    }

    // =========================
    // 想定参加人数
    // =========================
    if (this.showEstimatedParticipants) {
      if (
        this.form.estimatedParticipants == null
      ) {
        return false;
      }
    }

    // =========================
    // 想定費用総額
    // =========================
    if (this.showCostSection && this.form.categoryId != 10) {
      if (
        this.form.estimatedTotalCost == null
      ) {
        return false;
      }
    }

    // =========================
    // 補助金額
    // =========================
    if (this.showSubsidyAndVendors && this.form.categoryId != 10) {
      if (
        this.form.subsidyAmount == null
      ) {
        return false;
      }
    }

    // =========================
    // 該当職場の想定議題
    // =========================
    if (this.showWorkplaceSection) {
      if (!this.form.workplaceExpectedIssues?.trim()) {
        return false;
      }

      if (!this.form.keyHearingItems?.trim()) {
        return false;
      }
    }

    // =========================
    // 効果測定
    // =========================
    if (this.showEffectiveness && this.form.categoryId != 10) {
      if (this.form.effectivenessMeasurement == null) {
        return false;
      }
    }

    // =========================
    // 会議体・対応形式
    // =========================
    if (this.showMeetingSection) {
      if (!this.form.meetingFormats?.length) {
        return false;
      }

      if (!this.form.counterpartTypes?.length) {
        return false;
      }
    }

    return true;
  }
}
